import { useState, useEffect, useCallback } from 'react';
import { rpcProvider } from '../utils/MultiProviderRPC';

// Type definitions
interface ProviderStatus {
    status: 'healthy' | 'unhealthy' | 'unknown';
    lastSuccess?: number;
    lastFailure?: number;
    responseTime?: number;
    consecutiveFailures: number;
    lastError?: string;
}

interface ProviderStats {
    totalProviders: number;
    currentProvider: string;
    healthyProviders: number;
    unhealthyProviders: number;
    averageResponseTime: number;
}

interface HealthData {
    providers: Record<string, ProviderStatus>;
    stats: ProviderStats;
    lastUpdate: Date | null;
}

interface HealthySummary {
    total: number;
    healthy: number;
    unhealthy: number;
    healthPercentage: number;
}

/**
 * React hook for Multi-Provider RPC Client (Vite+React)
 * Provides health monitoring and convenient RPC methods for MEMER ecosystem
 */
export const useMultiProviderRPC = () => {
    const [healthStatus, setHealthStatus] = useState<Record<string, ProviderStatus>>({});
    const [providerStats, setProviderStats] = useState<ProviderStats>({
        totalProviders: 0,
        currentProvider: '',
        healthyProviders: 0,
        unhealthyProviders: 0,
        averageResponseTime: 0
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    // Update health status and stats periodically
    useEffect(() => {
        const updateStatus = () => {
            setHealthStatus(rpcProvider.getHealthStatus());
            setProviderStats(rpcProvider.getProviderStats());
        };

        // Initial update
        updateStatus();

        // Update every 10 seconds
        const interval = setInterval(updateStatus, 10000);

        return () => clearInterval(interval);
    }, []);

    // Generic request method with loading and error handling
    const makeRequest = useCallback(async (method: string, params: any[] = [], options: any = {}) => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await rpcProvider.makeRequest(method, params, options);
            return result;
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Convenience methods with error handling
    const getBalance = useCallback(async (address: string) => {
        try {
            const balance = await rpcProvider.getBalance(address);
            return balance;
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, []);

    const getBlockNumber = useCallback(async () => {
        try {
            const blockNumber = await rpcProvider.getBlockNumber();
            return parseInt(blockNumber, 16);
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, []);

    const call = useCallback(async (transaction: any) => {
        try {
            const result = await rpcProvider.call(transaction);
            return result;
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, []);

    const sendTransaction = useCallback(async (transaction: any) => {
        setIsLoading(true);
        setError(null);

        try {
            const txHash = await rpcProvider.sendTransaction(transaction);
            return txHash;
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const estimateGas = useCallback(async (transaction: any) => {
        try {
            const gasEstimate = await rpcProvider.estimateGas(transaction);
            return parseInt(gasEstimate, 16);
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, []);

    const getTransactionReceipt = useCallback(async (hash: string) => {
        try {
            const receipt = await rpcProvider.getTransactionReceipt(hash);
            return receipt;
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, []);

    const getTransactionCount = useCallback(async (address: string) => {
        try {
            const nonce = await rpcProvider.getTransactionCount(address);
            return parseInt(nonce, 16);
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }, []);

    // Health check utilities
    const isHealthy = useCallback(() => {
        return providerStats.healthyProviders > 0;
    }, [providerStats]);

    const getHealthyProviderCount = useCallback(() => {
        return providerStats.healthyProviders || 0;
    }, [providerStats]);

    const getCurrentProviderName = useCallback(() => {
        return providerStats.currentProvider || 'Unknown';
    }, [providerStats]);

    const getAverageResponseTime = useCallback(() => {
        return providerStats.averageResponseTime || 0;
    }, [providerStats]);

    // Clear error manually
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        // Core RPC methods
        makeRequest,
        getBalance,
        getBlockNumber,
        call,
        sendTransaction,
        estimateGas,
        getTransactionReceipt,
        getTransactionCount,

        // Status and monitoring
        healthStatus,
        providerStats,
        isLoading,
        error,
        clearError,

        // Health utilities
        isHealthy,
        getHealthyProviderCount,
        getCurrentProviderName,
        getAverageResponseTime,

        // Direct access to provider instance
        rpcProvider
    };
};

/**
 * Hook for monitoring RPC provider health with real-time updates
 * Now uses backend API for comprehensive health monitoring
 */
export const useRPCHealthMonitor = () => {
    const [healthData, setHealthData] = useState<HealthData>({
        providers: {},
        stats: {
            totalProviders: 0,
            currentProvider: '',
            healthyProviders: 0,
            unhealthyProviders: 0,
            averageResponseTime: 0
        },
        lastUpdate: null
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchHealthData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/providers/health');

            if (!response.ok) {
                throw new Error(`Health API error: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            // Transform backend response to match expected format
            setHealthData({
                providers: data.providers || {},
                stats: {
                    totalProviders: data.summary?.total || 0,
                    currentProvider: rpcProvider.getProviderStats().currentProvider, // Keep current provider from RPC instance
                    healthyProviders: data.summary?.healthy || 0,
                    unhealthyProviders: data.summary?.unhealthy || 0,
                    averageResponseTime: data.summary?.averageResponseTime || 0
                },
                lastUpdate: data.lastUpdate ? new Date(data.lastUpdate) : new Date()
            });

            // Reduced logging - only log when health status changes significantly
            const currentHealthy = data.summary?.healthy || 0;
            const currentTotal = data.summary?.total || 0;
            const previousHealthy = healthData.stats.healthyProviders;

            // Only log if health status changed or this is the first update
            if (previousHealthy !== currentHealthy || healthData.lastUpdate === null) {
                console.log(`🏥 Health data updated: ${currentHealthy}/${currentTotal} providers healthy`);
            }

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown health monitoring error';
            console.error('❌ Health monitoring fetch failed:', errorMessage);
            setError(errorMessage);

            // Fallback to local RPC provider stats if backend fails
            const fallbackStats = rpcProvider.getProviderStats();
            setHealthData({
                providers: rpcProvider.getHealthStatus(),
                stats: fallbackStats,
                lastUpdate: new Date()
            });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        // Initial fetch
        fetchHealthData();

        // Update every 30 seconds (backend has 30s cache)
        const interval = setInterval(fetchHealthData, 30000);

        return () => clearInterval(interval);
    }, [fetchHealthData]);

    const getProviderStatus = useCallback((providerName: string): ProviderStatus => {
        return healthData.providers[providerName] || { status: 'unknown', consecutiveFailures: 0 };
    }, [healthData.providers]);

    const getHealthySummary = useCallback((): HealthySummary => {
        const total = healthData.stats.totalProviders || 0;
        const healthy = healthData.stats.healthyProviders || 0;
        const unhealthy = healthData.stats.unhealthyProviders || 0;

        return {
            total,
            healthy,
            unhealthy,
            healthPercentage: total > 0 ? Math.round((healthy / total) * 100) : 0
        };
    }, [healthData.stats]);

    return {
        healthData,
        getProviderStatus,
        getHealthySummary,
        isMonitoring: healthData.lastUpdate !== null,
        isLoading,
        error
    };
};

/**
 * Hook for blockchain data with automatic retry and failover
 */
export const useBlockchainData = (method: string, params: any[] = [], options: any = {}) => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const { makeRequest } = useMultiProviderRPC();

    const fetchData = useCallback(async () => {
        if (!method) return;

        setLoading(true);
        setError(null);

        try {
            const result = await makeRequest(method, params, options);
            setData(result);
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            console.error(`Failed to fetch blockchain data for ${method}:`, error);
        } finally {
            setLoading(false);
        }
    }, [method, JSON.stringify(params), JSON.stringify(options), makeRequest]);

    // Auto-fetch on mount and when dependencies change
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Manual refetch
    const refetch = useCallback(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        loading,
        error,
        refetch
    };
};
